import React, { useEffect, useRef } from "react";
import { LockClosedIcon, CheckIcon } from "@heroicons/react/24/outline";

/**
 * Step panel wrapper for the Issuance Tool.
 *
 * Renders a card with a heading, optional description, and body. When
 * status is "disabled", the body is replaced with a placeholder
 * indicating the step is locked until the previous step completes.
 *
 * 2026-07-04 operator consistency directive ("titles don't follow the
 * same format... numbers like 1, 2, 3 differ to every other page") :
 * the numbered-circle badge is replaced by a plain text heading of the
 * form "Step 1 : Select Your Service" (Title Case, space-colon),
 * rendered in the SAME size / weight / coloring as the reference
 * section headings on the other tool pages - the
 * "text-lg font-semibold text-heading" idiom of the "Certificate
 * Request Information" heading in src/client/pages/CSRGenerator.jsx -
 * with the description paragraph on the matching text-body role. Both
 * read text-gray-900 / text-gray-600 until the 2026-08-25 role-colour
 * pass, there and here.
 * The element stays an <h2> so the document outline keeps its
 * page-h1 -> step-h2 -> section-h3/h4 order ; only the visual
 * treatment matches the reference h3 sections. The section's
 * aria-label keeps the same "Step N : Title" text as before, which now
 * also matches the visible heading verbatim, so assistive technology
 * announces one consistent name.
 *
 * 2026-07-05 operator directive ("After selecting Step 1 you go to
 * step 2 and make step one this huge long blue box... it is ugly in
 * this format") : a COMPLETED step no longer renders the full card
 * (heading + long description + a tall saturated bg-blue-50 slab of
 * selection details). It collapses to ONE compact summary row - a
 * slim light-NEUTRAL line (bg-gray-50, dark:bg-gray-900/50 - the
 * documented neutral-inset tier, deliberately NOT blue ; the inset
 * tone keeps a completed row visually RECESSED below the active
 * .card surface in BOTH themes, where the earlier dark:bg-gray-800
 * sat level with the cards in dark mode) carrying a small green
 * check indicator, the
 * "Step N : Title" heading, and the caller-supplied `summary` (the
 * customer's selection, e.g. "Trustico® Domain Validation (DV)
 * Selected"). The description paragraph and the step body are dropped
 * from the completed rendering entirely : each active section already
 * explains itself, so the collapsed row only needs to RECORD the
 * decision. The heading stays a real <h2> (inline inside the flex row)
 * so the page outline keeps every step level even when collapsed.
 * There is deliberately NO expand/reopen affordance : none existed
 * before this redesign either (completed panels were always static
 * summaries), so none is invented here.
 *
 * The `terminal` prop opts a step OUT of the collapse : Step 4 is
 * terminal and MUST keep rendering its children when completed,
 * because issuance success marks the step completed automatically and
 * collapsing it would unmount the issued SSL Certificate and Private
 * Key - the exact PR #76 review blocking bug (2026-07-03) that the
 * active-or-completed render branch in src/client/pages/Issuance.jsx
 * exists to prevent. A terminal step renders identically for "active"
 * and "completed".
 *
 * Dark mode (operator directive 2026-07-05, "if I click it then I
 * can't read any black text that didn't adapt") : nothing in this file
 * needs a dark: variant any more. It used to instruct one per raw
 * utility (text-gray-900 -> dark:text-white, text-gray-600 ->
 * dark:text-gray-300) ; as of 2026-08-25 the text here is on the role
 * names, which flip with the theme by themselves, so writing a dark:
 * half would be the mistake rather than the requirement. The `card`
 * class is shared and carries its own dark styling centrally.
 *
 * Props :
 *   number - the step number (1 to 4), rendered as plain heading text
 *   title - heading text shown after the "Step N : " prefix
 *   description - short paragraph rendered under the heading (optional)
 *   status - one of "active", "completed", "disabled"
 *   children - step content (rendered when status is not "disabled")
 *   disabledMessage - placeholder text shown when status is "disabled"
 *   summary - the customer's recorded selection, rendered inside the
 *             compact completed row (optional ; the row renders the
 *             heading + check alone when absent)
 *   terminal - when true the step NEVER collapses on completion (see
 *              the terminal rationale above)
 */
function StepPanel({
  number,
  title,
  description,
  status,
  children,
  disabledMessage,
  summary,
  terminal = false,
}) {
  const isDisabled = status === "disabled";

  // Keyboard focus follows step ACTIVATION (2026-08-14 go-public
  // accessibility review) : completing a step unmounts the button the
  // customer just pressed, which drops focus to <body>, so a
  // keyboard-only customer had to Tab from the very top of the
  // document (the header navigation and everything else above the
  // step) to find the next step - on every one of the four
  // transitions.
  // When THIS panel's status transitions INTO "active", focus moves
  // to its heading (the standard SPA step-wizard pattern). The
  // prev-status ref makes the first render a no-op by construction :
  // on page load / localStorage restore the panels FIRST render with
  // their final statuses (the page renders nothing until `restored`
  // flips), so no focus is ever stolen on arrival - only a
  // customer-initiated transition moves it.
  const headingRef = useRef(null);
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (status === "active" && prev !== "active" && headingRef.current) {
      headingRef.current.focus();
    }
  }, [status]);

  // A locked step is signalled by its muted heading and its lock line,
  // NOT by an opacity wash over the whole card. opacity-60 multiplied
  // down every colour inside, dropping the lock sentence to roughly
  // 2.6:1 against white - below the 4.5:1 contrast floor for body
  // text - and a translucent card reads as a rendering fault rather
  // than a deliberate state.
  const lockedClass = isDisabled ? "text-muted" : "text-heading";

  // The description recedes WITH its heading on a locked step. Muting
  // only the heading left the description rendering darker than the
  // title above it, which read as the more important of the two.
  const descriptionClass = isDisabled ? "text-muted" : "text-body";

  // Compact completed rendering (operator directive 2026-07-05) : one
  // slim neutral row, small vertical padding, never the full card.
  // Terminal steps skip this branch - see the `terminal` JSDoc above.
  if (status === "completed" && !terminal) {
    return (
      <section
        // flex-wrap : a long summary (e.g. a many-domain list on
        // Step 2) wraps to a second line instead of overflowing the
        // slim row ; the common case stays a single line.
        // px-6 (not px-4) so a completed step's title starts on the
        // SAME left edge as the title of the active card below it -
        // the active panel is `card p-6`, and at px-4 the headings
        // jogged 8px left as each step completed.
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-6 py-2.5 mb-6 flex flex-wrap items-center gap-x-3 gap-y-1"
        aria-label={`Step ${number} : ${title}`}
      >
        {/*
          aria-hidden : the check is a purely visual "done" cue ; the
          section's aria-label plus the collapsed (form-free) content
          already tell assistive technology the step is settled - the
          same posture the retired StepIndicator's check glyph had.
        */}
        <CheckIcon
          className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0"
          aria-hidden="true"
        />
        {/*
          Template literal (not adjacent JSX text nodes) so the
          "Step N : Title" spacing - space before the colon per the
          Trustico® house style - can never be reflowed away by a
          formatter. Same rationale as the active heading below.
        */}
        {/* whitespace-nowrap removed : "Step 2 : Configure SSL
            Certificate" cannot fit beside the check icon on a 320px
            viewport, and forbidding the wrap pushed the row into
            horizontal overflow instead of letting it use the second
            line the wrapper's flex-wrap already allows. */}
        <h2 className="text-sm font-semibold text-heading">
          {`Step ${number} : ${title}`}
        </h2>
        {/* Right-aligned muted summary (2026-08-15 design overhaul,
            operator mockup feedback : name left, selection right, so
            the row reads as two structured columns instead of words
            run together). min-w-0 + text-right keep a long
            multi-domain summary wrapping inside its own column. */}
        {summary && (
          <span className="text-sm text-muted min-w-0 ml-auto text-right">
            {summary}
          </span>
        )}
      </section>
    );
  }

  return (
    <section
      className="card p-6 mb-6"
      aria-label={`Step ${number} : ${title}`}
      aria-disabled={isDisabled || undefined}
    >
      <div className="mb-4">
        {/*
          Template literal (not adjacent JSX text nodes) so the
          "Step N : Title" spacing - space before the colon per the
          Trustico® house style - can never be reflowed away by a
          formatter.
        */}
        {/*
          tabIndex -1 : programmatically focusable for the
          step-activation focus move above, but never in the Tab
          order. The focus ring is VISIBLE on purpose (2026-08-14
          go-public review : suppressing it with focus:outline-hidden
          leaves a sighted keyboard-only customer with no indication
          of where focus landed after a step transition - their next
          Tab starts from an invisible position). The ring replaces
          the browser outline with the site's ring idiom and
          disappears on blur.
        */}
        {/*
          Heading and description tiers are the SITE canon, not a
          per-tool choice : every sibling tool card renders
          "text-lg font-semibold ... mb-2" over
          "text-body" body prose (see the
          section cards in src/client/pages/CSRGenerator.jsx and
          src/client/pages/PFXConverter.jsx). An earlier pass set this
          description to text-sm with mt-1, which made every step on
          this page render one size smaller and one notch tighter than
          the equivalent card on all ten other tools.
        */}
        <h2
          ref={headingRef}
          tabIndex={-1}
          className={`text-lg font-semibold ${lockedClass} mb-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 rounded-sm`}
        >
          {`Step ${number} : ${title}`}
        </h2>
        {/*
          A plain string stays wrapped in a single <p> so the common
          case keeps paragraph semantics. A step needing more than one
          paragraph (Step 1 carries the prerequisites list that used to
          sit in a separate card above the page) passes its own
          elements, and space-y-3 sets them the 12px apart every pair of
          stacked paragraphs on the site keeps (docs/UI_STANDARD.md).
        */}
        {description && (
          <div className={`${descriptionClass} space-y-3`}>
            {typeof description === "string" ? (
              <p>{description}</p>
            ) : (
              description
            )}
          </div>
        )}
      </div>

      {isDisabled ? (
        // No italic : this was the ONLY italic text in the whole
        // client tree, a typographic register that appears on no
        // sibling tool page. Base prose size, matching the
        // description directly above it, so one locked step does not
        // render two different text sizes.
        <div className="flex items-center gap-2 text-muted">
          <LockClosedIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
          {/* "unlocks once ..." matches the wording of every
              disabledMessage the page supplies ; the fallback used to
              say "after the previous step is completed", a second
              grammatical shape for the same idea. */}
          <span>
            {disabledMessage ||
              "This step unlocks once the previous step is complete."}
          </span>
        </div>
      ) : (
        children
      )}
    </section>
  );
}

export default StepPanel;
